<script lang="ts">
    import { onDestroy, onMount } from 'svelte'
    import { derived as sderived } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import Icon from '@iconify/svelte'
    import { DateTime } from 'luxon'

    import { type HydriaDocument, type IAttachment, type IAttachmentAltSize, type IImage, fileIcon } from 'hydria/HydriaDocument'
    import Date from 'hydria/components/Date.svelte'
    // import Dropzone from "hydria/components/Dropzone.svelte"
    import Dropzone from 'svelte-file-dropzone'
    import HumanizedBytes from 'hydria/components/HumanizedBytes.svelte'
    import { gettext as _ } from 'hydria/services/gettext'
    import { lg } from 'hydria/services/language'
    import { session } from 'hydria/services/session'
    import { asAny } from 'hydria/svelte_helpers'
    import { isEqual } from 'lodash-es'

    interface Props {
        Document: HydriaDocument
    }

    let { Document }: Props = $props()

    const images = Document.attachedImages
    const binaries = Document.attachedBinaries
    const allowedMimetypes = sderived([Document.allowedBinaryMimetypes, Document.allowedImageMimetypes], ([$bMimes, $iMimes]) => {
        if ($bMimes.has('all')) return ''
        return new Set([...$bMimes, ...$iMimes])
    })

    // IDs of the attachments being deleted
    let removing: Set<string> = $state(new Set())
    let removeConfirm: IAttachment | undefined = $state(undefined)

    function preRemove(att: IAttachment) {
        removeConfirm = att
    }
    async function remove(att: IAttachment) {
        removing.add(att.id)
        removing = removing
        removeConfirm = undefined

        await Document.deleteAttachment(att.id)

        removing.delete(att.id)
        removing = removing
    }

    interface LUpload {
        file: File
        progress: number
        started: DateTime
        ended?: DateTime
        status: 'uploading' | 'aborted' | 'finished' | 'error'
        xhr: XMLHttpRequest
        // formElement: HTMLFormElement
        // formData: FormData
    }
    let uploads: LUpload[] = $state([])

    onMount(() => {
        const interval = setInterval(() => {
            let loc = uploads.filter((upload) => upload.status == 'uploading' || !upload.ended || upload.ended > DateTime.now().minus({ seconds: 5 }))
            if (!isEqual(loc, uploads)) {
                uploads = loc
            }
        }, 250)

        return () => {
            clearInterval(interval)
        }
    })

    function abort(upload: LUpload) {
        if (upload.status == 'uploading') {
            upload.xhr.abort()
        }
    }

    function setupFileUpload(file: File) {
        // const formElement = document.createElement('form')
        const upload: LUpload = {
            file: file,
            progress: 0,
            status: 'uploading',
            started: DateTime.now(),
            xhr: new XMLHttpRequest(),
            // formElement: formElement,
            // formData: new FormData(formElement),
        }
        uploads.push(upload)
        let upos = uploads.length - 1

        const formData = new FormData()
        formData.append('file', file)

        const xhr = upload.xhr

        xhr.onreadystatechange = ((upos) => () => {
            if (xhr.readyState == XMLHttpRequest.DONE) {
                let resp: any = {
                    status: xhr.status,
                    data: null,
                }
                if (xhr.responseText) {
                    try {
                        resp.data = JSON.parse(xhr.responseText)
                    } catch (e) {}
                }

                uploads[upos].ended = DateTime.now()

                if (resp.status !== 200) {
                    uploads[upos].status = 'error'
                } else {
                    uploads[upos].status = 'finished'
                    Document.updateAttachmentList(resp.data.attachments, resp.data.newly_uploaded_id)
                }
            }
        })(upos)
        xhr.upload.addEventListener(
            'load',
            ((upos) => (e) => {
                uploads[upos].progress = 100
            })(upos),
            false,
        )
        xhr.upload.addEventListener(
            'progress',
            ((upos) => (e) => {
                uploads[upos].progress = Math.round((e.loaded * 100) / e.total)
            })(upos),
            false,
        )
        xhr.upload.addEventListener(
            'error',
            ((upos) => (e) => {
                uploads[upos].status = 'error'
                uploads[upos].ended = DateTime.now()
            })(upos),
            false,
        )
        xhr.upload.addEventListener(
            'abort',
            ((upos) => (e) => {
                uploads[upos].status = 'aborted'
                uploads[upos].ended = DateTime.now()
            })(upos),
            false,
        )
        xhr.open('POST', `/json/cms/1/upload/${Document.docType}/${Document.docId}`, true)
        xhr.setRequestHeader('X-Lg', $lg)
        // @ts-expect-error
        xhr.setRequestHeader('X-Hydria-Token', session.token)
        xhr.setRequestHeader('Accept', 'application/json')
        xhr.send(formData)
    }

    function handleFile(e: any) {
        // files = [...files, e.detail.acceptedFiles]
        if (!e.detail.acceptedFiles.length) return

        for (const file of e.detail.acceptedFiles as File[]) {
            setupFileUpload(file)
        }
    }

    function altImgSize(att: IAttachment, size: string): IAttachmentAltSize {
        if (att.mimetype == 'image/svg+xml') {
            return {
                alt_formats: [],
                sizename: null,
                filesize: att.filesize,
                url: att.url,
                mimetype: att.mimetype,
                width: att.image_info ? att.image_info.width : 0,
                height: att.image_info ? att.image_info.height : 0,
            }
        }
        if (!att.alt_image_sizes) return { url: '' } as IAttachmentAltSize

        const alt = att.alt_image_sizes.filter((alt) => alt.sizename == size).pop()

        if (!alt) return { url: '' } as IAttachmentAltSize
        return alt
    }

    $effect(() => {
        if (removeConfirm && !document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.add('modal-open')
        } else if (!removeConfirm && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })

    onDestroy(() => {
        if (removeConfirm && document.documentElement.classList.contains('modal-open')) document.documentElement.classList.remove('modal-open')
    })
</script>

<style>
    .dzone {
        display: flex;
        gap: 1rem;
    }
    .dzone > :nth-child(1) {
        flex: 0 0 auto;
    }
    .dzone > :nth-child(2) {
        flex: 1 1 0;
    }
    .up-progress {
        display: grid;
        grid-template-columns: 2fr 1fr auto;
        gap: 0.25rem 0.5rem;
        align-items: baseline;
    }
    .up-progress > :nth-child(3) {
        justify-self: end;
    }

    img {
        /* border: 1px solid var(--bs-border-color); */
        border-radius: 0.375rem;
        max-width: 100%;
        object-fit: contain;
        height: auto;
        box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.5);
    }
</style>

<div class="card card-primary card-content">
    <div class="card-header text-bg-primary">{$_('Upload')}</div>
    <div class="card-body">
        <div class="row">
            <div class="col-12 col-lg-6">
                <Dropzone on:drop={handleFile} accept={[...$allowedMimetypes].join(',')} multiple={true}>
                    <div class="dzone">
                        <div><Icon inline icon="mdi:image-size-select-actual" /></div>
                        <div>
                            {$_('Drag file(s) here from your desktop or file manager, or click to select files.')}
                        </div>
                    </div>
                </Dropzone>
            </div>
            <div class="col-12 col-lg-6">
                <div class="up-progress">
                    {#each uploads as upload, ui}
                        <div>
                            <span>{uploads[ui].file.name}</span>
                            <span><HumanizedBytes value={uploads[ui].file.size} /></span>
                        </div>
                        <div>
                            <div class="progress mb-0">
                                {#if uploads[ui].status == 'uploading'}
                                    <div class="progress-bar progress-bar-striped progress-bar-animated" style="min-width: 3em; width: {uploads[ui].progress}%" role="progressbar" aria-valuenow={uploads[ui].progress} aria-valuemin={0} aria-valuemax={100}>
                                        {uploads[ui].progress} %
                                    </div>
                                {:else if uploads[ui].status == 'aborted'}
                                    <div class="progress-bar bg-warning" style="width: 100%" role="progressbar" aria-valuenow={100} aria-valuemin={0} aria-valuemax={100}>
                                        {$_('Annulé')}
                                    </div>
                                {:else if uploads[ui].status == 'error'}
                                    <div class="progress-bar bg-danger" style="width: 100%" role="progressbar" aria-valuenow={100} aria-valuemin={0} aria-valuemax={100}>
                                        {$_('Error')}
                                    </div>
                                {:else if uploads[ui].status == 'finished'}
                                    <div class="progress-bar bg-success" style="width: 100%" role="progressbar" aria-valuenow={100} aria-valuemin={0} aria-valuemax={100}>100 %</div>
                                {/if}
                            </div>
                        </div>
                        <div>
                            {#if uploads[ui].status == 'uploading'}
                                <button type="button" class="btn btn-outline-secondary btn-sm" onclick={() => abort(uploads[ui])}>{$_('Annuler')}</button>
                                <!-- {:else if uploads[ui].status == 'finished'}
                                <button type="button" class="btn btn-success btn-xs" disabled>{$_('Terminé')}</button>
                            {:else if uploads[ui].status == 'aborted'}
                                <button type="button" class="btn btn-warning btn-xs" disabled>{$_('Annulé')}</button>
                            {:else if uploads[ui].status == 'error'}
                                <button type="button" class="btn btn-danger btn-xs" disabled>{$_('Erreur')}</button> -->
                            {/if}
                        </div>
                    {/each}
                </div>
            </div>
        </div>
    </div>
</div>

{#if $binaries.length}
    <div class="card card-content">
        <div class="card-header text-bg-primary">
            {$_('Fichiers connexes au document')}
        </div>
        <div class="card-body p-0">
            <table class="table">
                <thead>
                    <tr>
                        <th></th>
                        <th>{$_('Nom de fichier')}</th>
                        <th>{$_('Taille')}</th>
                        <th>{$_('Téléversement')}</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {#each $binaries as att, bi}
                        <tr>
                            <td class="text-center"><Icon inline icon={fileIcon($binaries[bi])} /></td>
                            <td><a href={$binaries[bi].url} target="_blank">{$binaries[bi].filename}</a></td>
                            <td><HumanizedBytes value={$binaries[bi].filesize} /></td>
                            <td><Date value={$binaries[bi].ctime} format="h-table-s" /></td>
                            <td class="text-right">
                                {#if !$binaries[bi].undeletable}
                                    {#if removing.has($binaries[bi].id)}
                                        <button type="button" class="btn btn-sm btn-info" disabled aria-hidden="true"><span class="spinner-border spinner-border-sm" role="status"></span></button>
                                    {:else}
                                        <button type="button" class="btn btn-sm btn-danger" onclick={() => preRemove($binaries[bi])}><Icon inline icon="mdi:delete" /></button>
                                    {/if}
                                {/if}
                            </td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>
    </div>
{/if}
{#if $images.length}
    <div class="card card-content">
        <div class="card-header text-bg-primary">
            {$_('Images connexes au document')}
        </div>
        <div class="card-body d-grid gap-2">
            {#each $images as att, ai}
                <div class="card">
                    <div class="card-body">
                        <div class="row">
                            <div class="col-4 col-md-3 col-lg-2">
                                <img src={altImgSize($images[ai], 'admin') ? altImgSize($images[ai], 'admin').url : ''} alt="" class="checkered" />
                            </div>
                            <div class="col-8 col-md-9 col-lg-10">
                                {#if !$images[ai].undeletable}
                                    {#if removing.has($images[ai].id)}
                                        <button type="button" class="btn btn-info btn-sm float-end" disabled aria-hidden="true"><span role="status" class="spinner-border spinner-border-sm"></span></button>
                                    {:else}
                                        <button type="button" class="btn btn-danger btn-sm float-end" onclick={() => preRemove($images[ai])}><Icon inline icon="mdi:delete" /></button>
                                    {/if}
                                {/if}
                                <p class="half-margin"><strong>{$images[ai].filename}</strong></p>
                                <p class="half-margin">
                                    <em>
                                        {#if $images[ai] && $images[ai].image_info}<span>
                                                {$images[ai].image_info.width} × {$images[ai].image_info.height}
                                                ;
                                            </span>{/if}<HumanizedBytes value={$images[ai].filesize} />
                                        ; <Date value={$images[ai].ctime} format="h-table-s" />
                                    </em>
                                </p>
                                <p class="no-margin">
                                    {$_('Alternates')} :
                                    <a href={$images[ai].url} target="_blank" class="btn btn-outline-secondary btn-sm">{$_('original')}</a>{#each $images[ai].alt_image_sizes as alt}{#if alt.sizename != 'admin'}<a href={alt.url} target="_blank" class="btn btn-outline-secondary btn-sm ms-1">{alt.sizename}</a>{/if}{/each}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            {/each}
        </div>
    </div>
{/if}

{#if removeConfirm}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header text-bg-primary">
                    <div>{$_('Suppression d’un fichier')}</div>
                </div>

                <div class="modal-body">
                    {$_('Êtes-vous certain de vouloir supprimer ce fichier ?')}
                </div>

                <div class="modal-footer">
                    <button class="btn btn-warning" type="button" onclick={() => remove(asAny(removeConfirm))}>
                        {$_('Supprimer')}
                    </button>
                    <button class="btn btn-outline-secondary" type="button" onclick={() => (removeConfirm = undefined)}>{$_('Annuler')}</button>
                </div>
            </div>
        </div>
        <div class="modal-backdrop" onclick={() => (removeConfirm = undefined)} aria-hidden="true"></div>
    </div>
{/if}
